import { AppData } from '@wix/document-services-types';
import { Container, UnifiedWidget } from '../../../../types/unifiedComponents';
import { IntentInstallStore } from '../IntentInstallStore';
import type { ProgressBarManager } from '../ProgressBarManager';
import { findAsyncSequential } from '../helpers';
import { loadPanel } from './loadPanel';
import type { IntentModalsProps, WidgetsConfiguration } from './ui/types';
import { IntentInstallPanelsNames } from './ui/types';
import { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';

const token = '';

type PanelPromiseCallback<T> = (resolve: (data: T) => void) => void;

export class PanelsManager {
  constructor(
    private platformApiMethods: PlatformApiMethods,
    private intentInstallStore: IntentInstallStore,
    private progressBarManager: ProgressBarManager,
  ) {}

  private async openPlatformPanel<T extends IntentInstallPanelsNames>(
    panelName: T,
    props: IntentModalsProps[T],
  ) {
    this.platformApiMethods.editor.openPanel({} as AppData, panelName, props, {
      // TODO: the commented out fields don't fit the type. Did i miss something?
      // name: panelName,
      // panelName,
      panelLoader: () => loadPanel(panelName),
      // componentRef: {},
      // openTime: Date.now(),
      // closeWithUserIntent: true,
      // frameType: 'platform',
    });
  }

  private createPanelPromise<T>(cb: PanelPromiseCallback<T>) {
    return new Promise<T>((resolve) => {
      const resumeProgressBar = this.progressBarManager.pauseProgressBar();

      const wrappedResolve = (value: T) => {
        resolve(value);
        resumeProgressBar();
      };
      cb(wrappedResolve);
    });
  }

  public openPreIntentInstallModal() {
    const intentApp = this.intentInstallStore.getIntentApp();

    if (!intentApp) {
      return;
    }

    const otherAppsToInstall =
      this.intentInstallStore.getAppNamesToInstallExceptIntentApp();

    if (!otherAppsToInstall.length) {
      return;
    }

    const intentAppName = this.intentInstallStore.getIntentAppName()!;

    return this.createPanelPromise<void>((resolve) => {
      this.openPlatformPanel(IntentInstallPanelsNames.PreIntentInstallModal, {
        intentAppName,
        otherAppsToInstall,
        closePanel: () => {
          this.platformApiMethods.editor.closePanel(
            {} as AppData,
            '',
            IntentInstallPanelsNames.PreIntentInstallModal,
          );
          resolve();
        },
      });
    });
  }

  public openWidgetAppsIntentInstallModal(
    widgets: UnifiedWidget[],
    pages: Container[],
    appName: string,
  ) {
    return this.createPanelPromise<WidgetsConfiguration>((resolve) => {
      this.openPlatformPanel(
        IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
        {
          appName,
          widgets,
          pages,
          onSubmit: (widgetsConfig: WidgetsConfiguration) => {
            this.platformApiMethods.editor.closePanel(
              {} as AppData,
              '',
              IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
            );
            resolve(widgetsConfig);
          },
          onCancel: () => {
            this.platformApiMethods.editor.closePanel(
              {} as AppData,
              '',
              IntentInstallPanelsNames.WidgetAppsIntentInstallModal,
            );
            resolve({});
          },
        },
      );
    });
  }

  public openPlatformFailedAlert() {
    const appsWithErrors = this.intentInstallStore.getAppsWithErrorsNames();
    if (!appsWithErrors.length) {
      return;
    }

    return this.platformApiMethods.editor.openPanel(
      {} as AppData,
      'savePublish.panels.common.failPanel',
      {
        titleKey: 'Notification_Box_App_Installation_Error_Title',
        description: 'Notification_Box_App_Installation_Error_Text',
        steps: [
          'Notification_Box_App_Installation_Error_Step1',
          'Notification_Box_App_Installation_Error_Step2',
        ],
        symbolName: 'serverError_NewWorkspace',
        helpMessage: '',
        helpLinkMessage: '',
        helpLink: '',
        translateArgs: {
          description: {
            appName: appsWithErrors.join(', '),
          },
        },
      },
      false,
    );
  }

  private async checkIfShouldOpenBusinessManager(_appDefId: string) {
    // TODO: This function always returns false. Do we need it? it's called by:
    // intent install service -> this.panelsManager.openBusinessPanel(); -> checkIfShouldOpenBusinessManager
    // this used to be based on an editor api called `isAppSupportedByBusinessManager` but this API isn't provided in any editor.
    // so can we remove the entire flow? or do we need to force the editors to provide this API?
    return false;
  }

  public async openBusinessPanel() {
    const isSilent = this.intentInstallStore.isSilent();

    const intentApp = this.intentInstallStore.getIntentApp();
    const appDefIds = isSilent
      ? this.intentInstallStore.getAppsToInstall()
      : intentApp?.appDefId
      ? [intentApp.appDefId]
      : [];

    const selectedAppId = await findAsyncSequential(appDefIds, (appDefId) =>
      this.checkIfShouldOpenBusinessManager(appDefId),
    );

    if (!selectedAppId) {
      return;
    }

    this.platformApiMethods.editor.openPanel(
      {} as AppData,
      'appManager.appManagerPanel',
      { selectedAppId },
      false,
    );
  }
}
